import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import LoadingScreen from './components/LoadingScreen';
import Seo from './components/Seo';

// Core layout components (always needed)
import Navbar from './components/Navbar';
import AdminNavbar from './components/AdminNavbar';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';
import HelperBot from './components/HelperBot';
import { CustomerAuthProvider } from './hooks/useCustomerAuth';
import CustomerLoginPopup from './components/CustomerLoginPopup';
import api from './services/api';
import { hasPlanFeature } from './utils/planFeatures';

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const OpenBusinessRequestPage = lazy(() => import('./pages/OpenBusinessRequestPage'));
const PaymentReturnPage = lazy(() => import('./pages/PaymentReturnPage'));
const ScheduleManagerPage = lazy(() => import('./pages/ScheduleManagerPage'));
const TempClosurePage = lazy(() => import('./pages/TempClosurePage'));
const StaffManagerPage = lazy(() => import('./pages/StaffManagerPage'));
const AnalyticsPage = lazy(() => import('./pages/Analytics'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
// PermanentCustomersPage merged into CustomersPage
const ServicesManagerPage = lazy(() => import('./pages/ServicesManagerPage'));
const MySitePage = lazy(() => import('./pages/MySitePage'));
const ExternalOrderLinkPage = lazy(() => import('./pages/ExternalOrderLinkPage'));
const PendingApprovalPage = lazy(() => import('./pages/PendingApprovalPage'));
const QueuePage = lazy(() => import('./pages/QueuePage'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const PendingApprovalsPage = lazy(() => import('./pages/PendingApprovalsPage'));
const BusinessProfilePage = lazy(() => import('./pages/BusinessProfilePage'));
const RatingPage = lazy(() => import('./pages/RatingPage'));
const HelpGuidePage = lazy(() => import('./pages/HelpGuidePage'));
const MyPlanPage = lazy(() => import('./pages/MyPlanPage'));
const PublicBusinessLandingPage = lazy(() => import('./pages/PublicBusinessLandingPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const NearMePage = lazy(() => import('./pages/NearMePage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const FindBarber2 = lazy(() => import('./pages/FindBarber2'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const MyBusinessesPage = lazy(() => import('./pages/MyBusinessesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminBusinesses = lazy(() => import('./pages/AdminBusinesses'));
const AdminBusinessProfilePage = lazy(() => import('./pages/AdminBusinessProfilePage'));
const AdminCities = lazy(() => import('./pages/AdminCities'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminBlockedCustomers = lazy(() => import('./pages/AdminBlockedCustomers'));
const AdminBusinessRequests = lazy(() => import('./pages/AdminBusinessRequests'));
const AdminMessageStats = lazy(() => import('./pages/AdminMessageStats'));


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [isPendingAuthenticated, setIsPendingAuthenticated] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark' || saved === 'light') return saved;
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    let cancelled = false;

    api.get('/auth/session')
      .then((res) => {
        if (cancelled) return;
        const session = res.data || {};

        if (session.admin) {
          localStorage.setItem('admin_token', 'session');
          localStorage.setItem('admin', JSON.stringify(session.admin));
          setIsAdminAuthenticated(true);
          setAdmin(session.admin);
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin');
          setIsAdminAuthenticated(false);
          setAdmin(null);
        }

        if (session.business) {
          localStorage.setItem('business_token', 'session');
          localStorage.setItem('business', JSON.stringify(session.business));
          setIsAuthenticated(true);
          setUser(session.business);
        } else {
          localStorage.removeItem('business_token');
          localStorage.removeItem('business');
          setIsAuthenticated(false);
          setUser(null);
        }

        if (session.pending) {
          localStorage.setItem('pending_token', 'session');
          localStorage.setItem('pending_request', JSON.stringify(session.pending));
          setIsPendingAuthenticated(true);
          setPendingRequest(session.pending);
        } else {
          localStorage.removeItem('pending_token');
          localStorage.removeItem('pending_request');
          setIsPendingAuthenticated(false);
          setPendingRequest(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin');
        localStorage.removeItem('business_token');
        localStorage.removeItem('business');
        localStorage.removeItem('pending_token');
        localStorage.removeItem('pending_request');
        setIsAdminAuthenticated(false);
        setAdmin(null);
        setIsAuthenticated(false);
        setUser(null);
        setIsPendingAuthenticated(false);
        setPendingRequest(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    const onBusinessUpdated = () => {
      try {
        const updated = localStorage.getItem('business');
        if (updated) setUser(JSON.parse(updated));
      } catch {
        // ignore
      }
    };
    window.addEventListener('business_updated', onBusinessUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('business_updated', onBusinessUpdated);
    };
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('business_token', 'session');
    localStorage.setItem('business', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handlePendingLogin = (token, reqData) => {
    localStorage.setItem('pending_token', 'session');
    localStorage.setItem('pending_request', JSON.stringify(reqData));
    setIsPendingAuthenticated(true);
    setPendingRequest(reqData);
  };

  const handleAdminLogin = (token, adminData) => {
    localStorage.setItem('admin_token', 'session');
    localStorage.setItem('admin', JSON.stringify(adminData));
    setIsAdminAuthenticated(true);
    setAdmin(adminData);
  };

  const handleLogout = () => {
    api.post('/auth/logout', { role: 'business' }).catch(() => {});
    localStorage.removeItem('business_token');
    localStorage.removeItem('business');
    setIsAuthenticated(false);
    setUser(null);
  };

  const handlePendingLogout = async () => {
    try {
      await api.post('/auth/logout', { role: 'pending' });
    } catch {
      // Clear local pending auth state even if the logout request fails.
    } finally {
      localStorage.removeItem('pending_token');
      localStorage.removeItem('pending_request');
      setIsPendingAuthenticated(false);
      setPendingRequest(null);
    }
  };

  const handleAdminLogout = () => {
    api.post('/auth/logout', { role: 'admin' }).catch(() => {});
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin');
    setIsAdminAuthenticated(false);
    setAdmin(null);
  };

  // Track when page content (images etc.) has fully painted
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (loading) return;                 // session still in-flight
    let rafId;
    let stableCount = 0;
    const STABLE_THRESHOLD = 5;          // need 5 consecutive "all loaded" frames

    const check = () => {
      const imgs = Array.from(document.querySelectorAll('img'));
      const allLoaded = imgs.length > 0 && imgs.every(img => img.complete);
      if (allLoaded) {
        stableCount++;
        if (stableCount >= STABLE_THRESHOLD) {
          setPageReady(true);
          return;
        }
      } else {
        stableCount = 0;                 // reset – new images appeared or still loading
      }
      rafId = requestAnimationFrame(check);
    };

    // Give React time to mount the real content (lazy chunks + data fetches)
    const delay = setTimeout(() => {
      rafId = requestAnimationFrame(check);
    }, 300);

    return () => {
      clearTimeout(delay);
      cancelAnimationFrame(rafId);
    };
  }, [loading]);

  function AppInner() {
    const location = useLocation();
    const pathname = (location.pathname || '');
    const isPublicLanding = pathname.startsWith('/b/');
    const isAuthRoute = pathname.startsWith('/login') || pathname.startsWith('/register');
    const isOpenBusinessRoute = pathname.startsWith('/open-business');
    const isPaymentRoute = pathname.startsWith('/payment-success') || pathname.startsWith('/payment-failure');

    const isAdminRoute = pathname.startsWith('/admin');
    const isPendingRoute = pathname.startsWith('/pending');
    const isSupportRoute = pathname.startsWith('/contact') || pathname.startsWith('/forgot-password') || pathname.startsWith('/terms') || pathname.startsWith('/privacy');
    const isBusinessRoute = (
      pathname === '/dashboard'
      || pathname.startsWith('/profile')
      || pathname.startsWith('/settings')
      || pathname.startsWith('/business-status')
      || pathname.startsWith('/schedule')
      || pathname.startsWith('/temp-closure')
      || pathname.startsWith('/appointments')
      || pathname.startsWith('/waitlist')
      || pathname.startsWith('/approvals')
      || pathname.startsWith('/services')
      || pathname.startsWith('/my-site')
      || pathname.startsWith('/external-order')
      || pathname.startsWith('/customers')
      || pathname.startsWith('/staff')
      || pathname.startsWith('/analytics')
      || pathname.startsWith('/rating')
      || pathname.startsWith('/my-plan')
      || pathname.startsWith('/help')
      || pathname.startsWith('/business/')
      || isPendingRoute
    );

    const renderPlanGuard = (featureKey, element) => (
      hasPlanFeature(user, featureKey)
        ? element
        : <Navigate to="/my-plan" replace state={{ upgradeFeature: featureKey, sourcePath: pathname }} />
    );

    const defaultHomeRoute = '/appointments';

    const isPublicRoute = (
      pathname === '/'
      || pathname.startsWith('/catalog')
      || pathname.startsWith('/articles')
      || pathname.startsWith('/near-me')
      || pathname.startsWith('/map')
      || pathname.startsWith('/my-businesses')
      || isPaymentRoute
      || isSupportRoute
    );

    const showAdminNavbar = !isPublicLanding && isAdminRoute && isAdminAuthenticated;
    const showBusinessNavbar = !isPublicLanding && !isAdminRoute && isBusinessRoute && isAuthenticated && !isAdminAuthenticated;
    const noAppointmentsMode = Boolean(user?.no_appointments_mode || user?.settings?.no_appointments_mode);
    const requiresPlanSelection = Boolean(user?.plan?.requires_plan_selection || user?.requires_plan_selection);
    const showPublicHeader = !isPublicLanding && (isOpenBusinessRoute || isPaymentRoute || isAuthRoute || isPublicRoute) && !isAdminRoute && !isBusinessRoute;
    const showPublicFooter = showPublicHeader;
    const hasFixedTopNav = showAdminNavbar || showBusinessNavbar;
    const suspenseFallback = (isBusinessRoute && isAuthenticated && !isAdminAuthenticated) || isPublicLanding
      ? <LoadingScreen ready={false} />
      : null;
    const defaultSeo = {
      title: 'תן לי תור - בית הספרים הישראלי. למצוא ולקבוע תור לספר והכל פה באתר',
      description: 'מערכת מקצועית לניהול תורים אוטומטי ב-WhatsApp. הלקוחות קובעים תור לבד, העסק חוסך זמן ומתנהל בצורה מסודרת ואמינה.',
    };
    const seo = pathname.startsWith('/privacy')
      ? {
        title: 'מדיניות פרטיות - תן לי תור | Tenlitor',
        description: 'מדיניות הפרטיות של Tenlitor: אילו פרטים נאספים, כיצד נעשה בהם שימוש, ואילו זכויות עומדות למשתמשים.',
      }
      : pathname.startsWith('/terms')
        ? {
          title: 'תנאי שימוש - תן לי תור | Tenlitor',
          description: 'תנאי השימוש של Tenlitor לשירות קביעת תורים וניהול תורים אוטומטי ב-WhatsApp.',
        }
        : pathname.startsWith('/contact') || pathname.startsWith('/forgot-password')
          ? {
            title: 'יצירת קשר - תן לי תור | Tenlitor',
            description: 'יצירת קשר עם Tenlitor לקבלת תמיכה, מידע נוסף וסיוע בשימוש במערכת.',
          }
          : defaultSeo;

    return (
      <>
        <Seo title={seo.title} description={seo.description} path={pathname || '/'} />
        {showPublicHeader && (
          <PublicHeader theme={theme} onToggleTheme={toggleTheme} />
        )}
        {showAdminNavbar && (
          <AdminNavbar admin={admin} onLogout={handleAdminLogout} theme={theme} onToggleTheme={toggleTheme} />
        )}
        {showBusinessNavbar && (
          <Navbar user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
        )}

        <div className={hasFixedTopNav ? 'app-content app-content--with-fixed-nav' : 'app-content'}>
          <Suspense fallback={suspenseFallback}>
          <Routes>
          {/* Public catalog available to everyone */}
          <Route path="/" element={<><LandingPage /><CustomerLoginPopup /></>} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/near-me" element={<NearMePage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/find2" element={<FindBarber2 />} />
          <Route path="/my-businesses" element={<MyBusinessesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/forgot-password" element={<ContactPage mode="forgot-password" />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/open-business" element={<OpenBusinessRequestPage />} />
          <Route path="/payment-success" element={<PaymentReturnPage />} />
          <Route path="/payment-failure" element={<PaymentReturnPage />} />
          <Route
            path="/login"
            element={
              isAdminAuthenticated ? (
                <Navigate to="/admin/dashboard" replace />
              ) : isPendingAuthenticated ? (
                <Navigate to="/pending" replace />
              ) : isAuthenticated ? (
                <Navigate to="/appointments" replace />
              ) : (
                <LoginPage onLogin={handleLogin} onAdminLogin={handleAdminLogin} onPendingLogin={handlePendingLogin} />
              )
            }
          />
          <Route path="/register" element={<Navigate to="/open-business" replace />} />
          {/* PUBLIC ROUTES (no login required) */}
          <Route
            path="/b/:businessKey"
            element={<PublicBusinessLandingPage />}
          />

          {/* ADMIN ROUTES */}
          {isAdminAuthenticated ? (
            <>
              <Route path="/admin/dashboard" element={<AdminDashboard admin={admin} />} />
              <Route path="/admin/requests" element={<AdminBusinessRequests />} />
              <Route path="/admin/businesses" element={<AdminBusinesses />} />
              <Route path="/admin/businesses/:businessId" element={<AdminBusinessProfilePage />} />
              <Route path="/admin/cities" element={<AdminCities />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/blocked-customers" element={<AdminBlockedCustomers />} />
              <Route path="/admin/message-stats" element={<AdminMessageStats />} />
              <Route path="/admin/services" element={<Navigate to="/admin/businesses" replace />} />
              <Route path="/admin/bot-flow" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/admin/dashboard" />} />
            </>
          ) : (
            <>
              {/* BUSINESS ROUTES */}
              {!isAuthenticated && !isPendingAuthenticated ? (
                <>
                  {/* Legacy routes: keep URLs working but use the unified login page */}
                  <Route path="/admin/login" element={<Navigate to="/login" replace />} />
                  <Route path="/business/login" element={<Navigate to="/login" replace />} />
                  <Route path="*" element={<Navigate to="/login" />} />
                </>
              ) : (
                <>
                  {isPendingAuthenticated ? (
                    <>
                      <Route path="/pending" element={<PendingApprovalPage request={pendingRequest} onLogout={handlePendingLogout} />} />
                      <Route path="*" element={<Navigate to="/pending" replace />} />
                    </>
                  ) : requiresPlanSelection ? (
                    <>
                      <Route path="/my-plan" element={<MyPlanPage user={user} />} />
                      <Route path="/help" element={<HelpGuidePage />} />
                      <Route path="*" element={<Navigate to="/my-plan" replace />} />
                    </>
                  ) : (
                    <>
                      <Route path="/dashboard" element={<Navigate to={defaultHomeRoute} replace />} />
                      <Route path="/profile" element={<Navigate to="/settings" replace />} />
                      <Route path="/settings" element={renderPlanGuard('no-appointments-mode', <BusinessProfilePage user={user} />)} />
                      <Route path="/business-status" element={noAppointmentsMode ? renderPlanGuard('business-status', <QueuePage forceStatusMode />) : <Navigate to="/appointments" replace />} />
                      <Route path="/schedule" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('schedule', <ScheduleManagerPage user={user} />)} />
                      <Route path="/temp-closure" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('temp-closure', <TempClosurePage />)} />
                      <Route path="/appointments" element={renderPlanGuard('appointments', <QueuePage />)} />
                      <Route path="/approvals" element={renderPlanGuard('approvals', <PendingApprovalsPage />)} />
                      <Route path="/waitlist" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('waitlist', <WaitlistPage />)} />
                      <Route path="/services" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('services', <ServicesManagerPage />)} />
                      <Route
                        path="/my-site"
                        element={renderPlanGuard('external-order-link', <MySitePage user={user} />)}
                      />
                      <Route path="/external-order" element={renderPlanGuard('external-order-link', <ExternalOrderLinkPage user={user} />)} />
                      <Route path="/customers" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('customers', <CustomersPage user={user} />)} />
                      <Route path="/staff" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('staff', <StaffManagerPage user={user} />)} />
                      <Route path="/analytics" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('analytics', <AnalyticsPage user={user} />)} />
                      <Route path="/rating" element={noAppointmentsMode ? <Navigate to="/appointments" replace /> : renderPlanGuard('appointments', <RatingPage />)} />
                      <Route path="/my-plan" element={<MyPlanPage user={user} />} />
                      <Route path="/help" element={<HelpGuidePage />} />
                      <Route path="/permanent-customers" element={<Navigate to="/customers" replace />} />
                      <Route path="/business/dashboard" element={<Navigate to={defaultHomeRoute} replace />} />
                      <Route path="*" element={<Navigate to={defaultHomeRoute} replace />} />
                    </>
                  )}
                </>
              )}
            </>
          )}
          </Routes>
          </Suspense>
        </div>
        {showPublicFooter && <PublicFooter />}
        {showBusinessNavbar && <HelperBot user={user} />}
      </>
    );
  }

  return (
    <Router>
      <CustomerAuthProvider>
        <LoadingScreen ready={pageReady} />
        {/* Fixed scissors icon in top-right */}
        <div className="global-scissors" aria-hidden>✂️</div>
        {!loading && <AppInner />}
      </CustomerAuthProvider>
    </Router>
  );
}

export default App;
